import { create } from 'zustand'
import { authApi } from '@/api/authApi'
import { ApiError, onAuthExpired } from '@/api/httpClient'
import { resetSessionState } from '@/stores/sessionReset'
import type {
  LoginRequest,
  Membership,
  MeResponse,
  RegisterRequest,
  User,
} from '@/api/types'

export type AuthStatus = 'idle' | 'loading' | 'authed' | 'expired' | 'guest'

interface AuthState {
  user: User | null
  memberships: Membership[]
  status: AuthStatus
  error: string | null

  loadMe: () => Promise<void>
  login: (body: LoginRequest) => Promise<void>
  register: (body: RegisterRequest) => Promise<void>
  verifyEmail: (code: string) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (body: { name?: string; email?: string; password?: string; current_password?: string }) => Promise<void>
  reset: () => void
  _bindAuthExpired: () => void
}

let authExpiredBound = false

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  memberships: [],
  status: 'idle',
  error: null,

  async loadMe() {
    const current = get().status
    if (current === 'loading') return
    set({ status: 'loading', error: null })
    try {
      const me = await authApi.me()
      const previousUserId = get().user?.id ?? null
      if (previousUserId !== null && previousUserId !== me.id) {
        await resetSessionState()
      }
      set({
        user: toUser(me),
        memberships: me.memberships,
        status: 'authed',
        error: null,
      })
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ user: null, memberships: [], status: 'guest', error: null })
        return
      }
      set({ status: 'guest', error: errMsg(err) })
    }
  },

  async login(body) {
    set({ status: 'loading', error: null })
    try {
      await authApi.login(body)
      // Reset to idle before loadMe() so its loading-guard doesn't bail early
      set({ status: 'idle' })
      await get().loadMe()
    } catch (err) {
      set({ status: 'guest', error: errMsg(err) })
      throw err
    }
  },

  async register(body) {
    set({ status: 'loading', error: null })
    try {
      await authApi.register(body)
      set({ status: 'idle' })
      await get().loadMe()
    } catch (err) {
      set({ status: 'guest', error: errMsg(err) })
      throw err
    }
  },

  async verifyEmail(code) {
    const me = await authApi.verifyEmail(code)
    set({ user: toUser(me), memberships: me.memberships, status: 'authed', error: null })
  },

  async logout() {
    const { useDraftStore } = await import('@/stores/draftStore')
    if (useDraftStore.getState().isDirty) {
      const confirmed = window.confirm('Você tem alterações não salvas. Sair mesmo assim?')
      if (!confirmed) return
    }
    try {
      await authApi.logout()
    } catch {
      /* ignore — limpamos estado local de qualquer forma */
    }
    await resetSessionState()
    get().reset()
  },

  async updateProfile(body) {
    const me = await authApi.updateMe(body)
    // Rebuilt through toUser() so flags like `email_verified` survive the update —
    // changing the address un-confirms it, and the guards read that field.
    set({ user: toUser(me), memberships: me.memberships })
  },

  reset() {
    set({ user: null, memberships: [], status: 'guest', error: null })
  },

  _bindAuthExpired() {
    if (authExpiredBound) return
    authExpiredBound = true
    onAuthExpired(() => {
      set({ user: null, memberships: [], status: 'expired' })
    })
  },
}))

useAuthStore.getState()._bindAuthExpired()

function toUser(me: MeResponse): User {
  return {
    id: me.id,
    name: me.name,
    email: me.email,
    is_superuser: me.is_superuser === true,
    is_staff: me.is_staff === true,
    email_verified: me.email_verified === true,
  }
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Erro desconhecido'
}
